import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { formatTutarTR } from '../../utils/para';

// PR-C (PC5a) — Satış Faturaları listesi.
// /api/satis-faturalari (GET) + iptal aksiyonu. Müşteri adı için /api/cari?tip=MUSTERI eşlemesi.
// Detay satır içinde açılır (GET /api/satis-faturalari/{id} → snapshot kalemler).
// İptal yalnız KESILDI'ye; tahsilatlı fatura 409 döner → toast.error(extractApiError).
// Anayasa M.5: MANAGE_SATIS ile mutation butonu gizleme (backend zaten enforce eder).

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Durum → rozet rengi (TASLAK gri, KESILDI yeşil, IPTAL kırmızı)
const DURUM_BADGE = {
  TASLAK: { bg: '#e2e3e5', fg: '#383d41', label: 'Taslak' },
  KESILDI: { bg: '#d4edda', fg: '#155724', label: 'Kesildi' },
  IPTAL: { bg: '#f8d7da', fg: '#721c24', label: 'İptal' },
};

const DURUM_FILTRE = [
  { value: '', label: 'Tümü' },
  { value: 'TASLAK', label: 'Taslak' },
  { value: 'KESILDI', label: 'Kesildi' },
  { value: 'IPTAL', label: 'İptal' },
];

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const DurumBadge = ({ durum }) => {
  const b = DURUM_BADGE[durum] || { bg: colors.backgroundAlt, fg: colors.textSecondary, label: durum || '-' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
      background: b.bg, color: b.fg, whiteSpace: 'nowrap',
    }}>
      {b.label}
    </span>
  );
};

const SatisFaturalariPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [cariMap, setCariMap] = useState({}); // cari_id → unvan
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // aksiyon süren satır
  const [detayId, setDetayId] = useState(null); // açık detay satırı
  const [detay, setDetay] = useState(null); // GET /{id} sonucu (kalemler)
  const [detayLoading, setDetayLoading] = useState(false);

  const [durum, setDurum] = useUrlState('fat_durum', '');
  const [arama, setArama] = useUrlState('fat_q', '', { debounceMs: 300 });

  const token = localStorage.getItem('access_token');

  // Müşteri eşlemesi (cari_id → unvan). Bir kez yüklenir.
  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI&include_inactive=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach((c) => { map[c.id] = c.unvan; });
        setCariMap(map);
      })
      .catch(() => {});
  }, [token]);

  const fetchRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (durum) params.set('durum', durum);
      const res = await fetch(`${API}/api/satis-faturalari?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Satış faturası yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, durum, toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const cariAd = (id) => cariMap[id] || `#${id}`;

  const handleDetay = async (fat) => {
    // Toggle: aynı satıra tekrar tıklanırsa kapat
    if (detayId === fat.id) {
      setDetayId(null);
      setDetay(null);
      return;
    }
    setDetayId(fat.id);
    setDetay(null);
    setDetayLoading(true);
    try {
      const res = await fetch(`${API}/api/satis-faturalari/${fat.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDetay(data);
      } else {
        toast.error(extractApiError(data.detail));
        setDetayId(null);
      }
    } catch (err) {
      logError('Fatura detay hatası:', err);
      toast.error('Bağlantı hatası');
      setDetayId(null);
    } finally {
      setDetayLoading(false);
    }
  };

  const handleIptal = async (fat) => {
    if (!window.confirm(
      `"${fat.fatura_no}" faturasını iptal etmek istediğinize emin misiniz?\n\nİrsaliyeler tekrar faturalanabilir duruma döner.`,
    )) return;
    setBusyId(fat.id);
    try {
      const res = await fetch(`${API}/api/satis-faturalari/${fat.id}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${fat.fatura_no}" iptal edildi`);
        if (detayId === fat.id) { setDetayId(null); setDetay(null); }
        fetchRows();
      } else {
        // 409: tahsilatı başlamış fatura iptal edilemez
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Fatura iptal hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  // İstemci-taraf arama: fatura_no veya müşteri adı (liste ucu zaten 500 ile sınırlı)
  const gorunenRows = rows.filter((r) => {
    if (!arama) return true;
    const q = arama.toLocaleLowerCase('tr-TR');
    const no = (r.fatura_no || '').toLocaleLowerCase('tr-TR');
    const ad = (cariMap[r.cari_id] || '').toLocaleLowerCase('tr-TR');
    return no.includes(q) || ad.includes(q);
  });

  if (loading && rows.length === 0) {
    return (
      <div>
        <Header title="Satış Faturaları" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Satış Faturaları" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + yeni butonu */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>🧾 Satış Faturaları</h2>
          {canManage && (
            <Link
              to="/satislar/fatura/yeni"
              style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap', textDecoration: 'none' }}
            >
              + Yeni Fatura
            </Link>
          )}
        </div>

        {/* Filtreler: durum + arama */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          <select
            value={durum}
            onChange={(e) => setDurum(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px', fontWeight: 'bold' }}
          >
            {DURUM_FILTRE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Fatura no veya müşteri ara…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            style={{ flex: 1, minWidth: '250px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
          />
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Fatura No</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Müşteri</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Vade</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Tutar</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', width: '180px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {gorunenRows.map((fat) => (
                  <React.Fragment key={fat.id}>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{fat.fatura_no || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{fmtTarih(fat.tarih)}</td>
                      <td style={{ padding: '10px 12px' }}><strong>{cariAd(fat.cari_id)}</strong></td>
                      <td style={{ padding: '10px 12px' }}>{fmtTarih(fat.vade_tarihi)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {fat.toplam_brut != null ? `${formatTutarTR(fat.toplam_brut)} TL` : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}><DurumBadge durum={fat.durum} /></td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleDetay(fat)}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: detayId === fat.id ? colors.backgroundAlt : 'white', color: colors.textPrimary, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            {detayId === fat.id ? 'Gizle' : 'Detay'}
                          </button>
                          {canManage && fat.durum === 'KESILDI' && (
                            <button
                              onClick={() => handleIptal(fat)}
                              disabled={busyId === fat.id}
                              style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: busyId === fat.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                            >
                              İptal
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {detayId === fat.id && (
                      <tr style={{ borderBottom: `1px solid ${colors.border}`, background: colors.backgroundAlt }}>
                        <td colSpan={7} style={{ padding: '12px 16px' }}>
                          {detayLoading && <div style={{ color: colors.textSecondary, fontSize: '13px' }}>Detay yükleniyor…</div>}
                          {!detayLoading && detay && (
                            <div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Malzeme</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Miktar</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Birim</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Fiyat</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>KDV %</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Net</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Brüt</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(detay.kalemler || []).map((k) => (
                                    <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                      <td style={{ padding: '6px 8px' }}>{k.malzeme_ad_snapshot || `#${k.malzeme_id}`}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.miktar)}</td>
                                      <td style={{ padding: '6px 8px' }}>{k.birim || '-'}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.birim_fiyat)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{k.kdv_orani}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.satir_net)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTutarTR(k.satir_brut)}</td>
                                    </tr>
                                  ))}
                                  {(detay.kalemler || []).length === 0 && (
                                    <tr><td colSpan={7} style={{ padding: '10px', color: colors.textTertiary }}>Kalem yok.</td></tr>
                                  )}
                                </tbody>
                              </table>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '8px', fontSize: '13px' }}>
                                <span style={{ color: colors.textSecondary }}>Net: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(detay.toplam_net)} TL</strong></span>
                                <span style={{ color: colors.textSecondary }}>KDV: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(detay.toplam_kdv)} TL</strong></span>
                                <span style={{ color: colors.textPrimary }}>Genel: <strong style={{ fontFamily: 'monospace', color: styles.primary }}>{formatTutarTR(detay.toplam_brut)} TL</strong></span>
                              </div>
                              {detay.notlar && (
                                <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>Not: {detay.notlar}</div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {gorunenRows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                      {arama || durum ? 'Kritere uygun fatura bulunamadı.' : 'Henüz satış faturası kesilmemiş.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SatisFaturalariPage;
